import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { Button, ClickEvent, DataHeader, DataSourceExecutionEvent, DataSourceMode, EditRowDecorator, Layout, LookupModelSelectionEvent, TableRow, TableRowDisplayEvent, TableRowMode, Textbox, Toast } from "@mcleod/components";
import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutFAKProfile } from "./autogen/AutogenLayoutFAKProfile";
import { BuildRangeFAKProfile } from "./BuildRangeFAKProfile";
import { DuplicateId, DuplicateIdProps } from "./DuplicateId";

export class FAKProfile extends AutogenLayoutFAKProfile {

    private buildRangeLayout = Layout.getLayout("lme/dispatch/BuildRangeFAKProfile") as BuildRangeFAKProfile;
    private buttonGenerate = this.createGenerateButton();
    private existingNmfcSet: Set<string> = new Set();

    override onLoad(): void | Promise<void> {
        this.validateFakProfileRecordExist();
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.showDelete = true;
        dataHeader.promptToSaveChanges();
    }

    private validateFakProfileRecordExist() {
        const fakClassProfileUrl = "lme/dispatch/FAKProfile?mode=update&key=";
        const validateProps: DuplicateIdProps = { tableName: "fak_profile", screenName: "FAKProfile", navigateToUrl: fakClassProfileUrl };
        DuplicateId.configureIdTextbox(this.textboxId, validateProps);
    }

    tableNmfcOverrideOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow: ModelRow = tableRow.data as ModelRow;
        this.addScreenCodesHoverToDiscover(tableRow, modelRow);
    }

    private addScreenCodesHoverToDiscover(tableRow: TableRow, modelRow: ModelRow<any>) {
        const components = [
            { componentId: "textboxFromNmfcClassCode", fieldName: "from_nmfc_class_code" },
            { componentId: "textboxToNmfcClassCode", fieldName: "to_nmfc_class_code" },
        ];
        components.forEach(({ componentId, fieldName }) => {
            const fieldCode = modelRow.get(fieldName);
            const textbox = tableRow.findComponentById(componentId) as Textbox;
            this.implementScreenCodesHoverToDiscover(fieldCode, textbox);
        });
    }

    textboxNmfcClassCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const textboxComponent: Textbox = event.target as Textbox;
            const fieldCode = textboxComponent.text;
            this.implementScreenCodesHoverToDiscover(fieldCode, textboxComponent);
        }
    }

    implementScreenCodesHoverToDiscover(fieldCode: string, textbox: Textbox) {
        if (fieldCode && !StringUtil.isEmptyString(fieldCode)) {
            CommonTooltips.setTooltipContentLayout(textbox, null, "lme/dispatch/ScreenCodesQuickInfo", null, { field_code: fieldCode, field_name: "nmfc_class_code" });
        }
    }

    async imageRatingClassOnClick(event: ClickEvent) {
        const fakClassCode = this.activeRow?.data?.id;
        if (StringUtil.isEmptyString(fakClassCode)) {
            CommonDialogs.showMessage("Please enter code before overriding the FAK Class Code", "Missing FAK Profile Code");
            return;
        }

        if (DataSourceMode.ADD === this.mainDataSource.mode) {
            const response = await Api.search("lme/dispatch/validate-fak-profile-exist", { fak_profile: fakClassCode, check_duplicate_id: true });
            const duplicateIdFound: boolean = response?.data[0]?.duplicate_id_found;
            if (duplicateIdFound) {
                const message: string = response?.data[0]?.message;
                CommonDialogs.showError(message);
                return;
            }
        }

        this.showBuildFakRangeSlider();
    }

    private showBuildFakRangeSlider() {
        const layout = this.buildRangeLayout;
        return new EditRowDecorator({
            title: "Build FAK Range",
            width: '50%',
            layout: layout,
            addlComponents: this.buttonGenerate,
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            fillVerticalSpace: true,
            onClose: () => {
                layout.resetFakRangeInSlideOut();
                layout.slideOut();
            },
        });
    }

    private createGenerateButton(): Button {
        return new Button({
            backgroundColor: "primary", borderColor: "primary", borderWidth: 1,
            caption: "Generate", color: "primary.reverse", rowBreak: false,
            tooltip: "Generate NFMC Override Enteries", onClick: (event: ClickEvent) => this.generateProfileRange()
        });
    }

    async generateProfileRange(): Promise<void> {
        this.buttonGenerate.busy = true;
        const fakClassCode = this.activeRow?.data?.id;
        const { actualLow, actualHigh, rated } = this.buildRangeLayout.getRequestBody();
        if (this.buildRangeLayout.validateRequiredFields(actualLow, actualHigh, rated)) {
            this.buttonGenerate.busy = false;
            return;
        }

        try {
            const endpoint = "lme/dispatch/build-range-fak-profile";
            const requestBody = { "fak_profile": fakClassCode, "actual_low_class": actualLow, "actual_high_class": actualHigh, "rated_class": rated };
            const response = await Api.post(endpoint, requestBody);
            const isSuccess: boolean = response?.data[0]?.success;
            const message: string = response?.data[0]?.message;
            if (!isSuccess) {
                this.buildRangeLayout.whenValidationFailedResetTextbox(response, message);
                this.buttonGenerate.busy = false;
                return;
            }

            this.buttonGenerate.busy = false;
            const mapFscDetails = response.data[0].new_detail_rows;
            const modelRows: ModelRow[] = mapFscDetails.map((row: any) => new ModelRow(this.sourceNmfcClassOverride.url, true, row));

            if (this.checkForDuplicateNmfcClassCode(modelRows)) {
                this.buildRangeLayout.resetFakRangeInSlideOut();
                return;
            }

            this.addNewRowsToTable(modelRows);
            this.buildRangeLayout.slideOut();
            this.buildRangeLayout.resetFakRangeInSlideOut();
            setTimeout(() => { Toast.showSuccessToast("Success", message) }, 500);

        } catch (error) {
            this.buttonGenerate.busy = false;
            this.buildRangeLayout.resetFakRangeInSlideOut();
            CommonDialogs.showError(error);
        }
    }

    /**
     * Method to check if there are duplicate NMFC class codes in the rows
     * @param modelRows The rows to be checked for duplicates
     * @returns true if there is a duplicate, false otherwise
     */
    private checkForDuplicateNmfcClassCode(modelRows: ModelRow[]): boolean {
        for (const row of modelRows) {
            const fromNmfc: string = row.get("from_nmfc_class_code");
            if (fromNmfc && this.existingNmfcSet.has(fromNmfc)) {
                CommonDialogs.showError(`An entry with NMFC class code ${fromNmfc} already exists.`);
                return true;
            }
            if (fromNmfc) this.existingNmfcSet.add(fromNmfc);
        }
        return false;
    }

    private addNewRowsToTable(modelRows: ModelRow<any>[]) {
        for (const row of modelRows) {
            const rowProps = { mode: TableRowMode.NONE };
            const options = { addToData: true, display: true, save: false };
            this.tableNmfcOverride.addRow(row, rowProps, options);
        }
    }

    sourceFakProfileAfterExecution(event: DataSourceExecutionEvent) {
        const fakClassCode = this.activeRow?.data?.id;
        if (!StringUtil.isEmptyString(fakClassCode)) {
            this.imageRatingClass.enabled = true;
            this.imageRatingClass.visible = true;
            this.imageRatingClass.color = 'primary';
            this.imageRatingClass.tooltip = "Generate NMFC Override Enteries for Profile: " + fakClassCode;
        }
    }
}
